/**
 * NFC入退室の通知モード管理hook
 *
 * モード:
 * - 'modal': モーダル表示（従来動作）
 * - 'notification': Windows通知バナー表示
 */

import { useState, useCallback } from 'react';
import { showNotification } from '@mantine/notifications';

export type NfcNotificationMode = 'modal' | 'notification';

const STORAGE_KEY = 'nfc_notification_mode';

function getStoredMode(): NfcNotificationMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'notification' ? 'notification' : 'modal';
}

export function useNfcNotificationMode() {
  const [mode, setModeState] = useState<NfcNotificationMode>(getStoredMode);

  const setMode = useCallback((newMode: NfcNotificationMode) => {
    localStorage.setItem(STORAGE_KEY, newMode);
    setModeState(newMode);
  }, []);

  const toggleMode = useCallback(async () => {
    const currentMode = getStoredMode();

    if (currentMode === 'modal') {
      // 通知モードに切り替え → 許可を要求
      if (!('Notification' in window)) {
        showNotification({
          title: '未対応',
          message: 'このブラウザはデスクトップ通知に対応していません',
          color: 'orange',
        });
        return;
      }

      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      if (permission === 'granted') {
        setMode('notification');
        showNotification({
          title: '通知モード',
          message: 'NFC入退室をデスクトップ通知で表示します',
          color: 'blue',
          autoClose: 2000,
        });
      } else {
        showNotification({
          title: '通知が許可されていません',
          message: 'ブラウザの設定から通知を許可してください',
          color: 'orange',
        });
      }
    } else {
      // モーダルモードに切り替え
      setMode('modal');
      showNotification({
        title: 'モーダルモード',
        message: 'NFC入退室をモーダルで表示します',
        color: 'teal',
        autoClose: 2000,
      });
    }
  }, [setMode]);

  return { mode, toggleMode } as const;
}

/**
 * CustomerNFCHandler内で使うためのユーティリティ（hookの外から参照可能）
 */
export function getNfcNotificationMode(): NfcNotificationMode {
  return getStoredMode();
}

export function canSendBrowserNotification(): boolean {
  return 'Notification' in window && Notification.permission === 'granted';
}
